import Sortable from "../vendor/sortable.esm"
import { showBackofficeFlash } from "packs/init-backoffice-flashes"
import { initBackofficeFileInputs } from "packs/init-backoffice-file-inputs"
import { confirmBackofficeDelete } from "packs/init-backoffice-confirm"

const csrfToken = () =>
  document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")

const emptyMessage = (root) =>
  root.dataset.emptyMessage || "Aucun document pour le moment."

const supportsStartupVisibility = (root) => root?.dataset.startupVisibility === "true"

const isSortableRoot = (root) => root?.dataset.contentDocsSortable === "true"

const ensureList = (root) => {
  let list = root.querySelector("[data-content-docs-list]")
  if (list) return list

  list = document.createElement("ul")
  list.className = "backoffice-list"
  list.dataset.contentDocsList = "true"
  if (isSortableRoot(root)) {
    list.classList.add("backoffice-list--droppable")
    list.dataset.contentDocsSortableList = "true"
  }
  const empty = root.querySelector("[data-content-docs-empty]")
  const anchor =
    empty ||
    root.querySelector("[data-content-docs-add-wrap]") ||
    root.querySelector("form") ||
    root.querySelector(".backoffice-form__row")
  if (anchor) root.insertBefore(list, anchor)
  else root.appendChild(list)
  if (isSortableRoot(root)) bindDocumentsSortable(list)
  return list
}

const syncEmptyState = (root) => {
  if (!root) return
  const list = root.querySelector("[data-content-docs-list]")
  const count = list?.querySelectorAll("[data-content-document-id]").length || 0
  let empty = root.querySelector("[data-content-docs-empty]")
  if (!empty && count === 0) {
    empty = document.createElement("p")
    empty.className = "backoffice-muted"
    empty.dataset.contentDocsEmpty = "true"
    empty.textContent = emptyMessage(root)
    const anchor =
      root.querySelector("[data-content-docs-add-wrap]") ||
      root.querySelector("form") ||
      root.querySelector(".backoffice-form__row")
    if (list?.nextSibling) root.insertBefore(empty, list.nextSibling)
    else if (anchor) root.insertBefore(empty, anchor)
    else root.appendChild(empty)
  }
  if (empty) empty.hidden = count > 0

  const hint = root.querySelector("[data-content-docs-hint]")
  if (hint) hint.hidden = count <= 1

  root.querySelectorAll("[data-content-document-handle]").forEach((handle) => {
    handle.hidden = count <= 1
  })
}

const STARTUP_TOOLTIP_OFF = "Activez pour rendre ce document visible aux start-ups"
const STARTUP_TOOLTIP_ON =
  "Désactivez pour ne pas afficher ce document dans l'espace start-up"

const setStartupToggleState = (toggle, on) => {
  const tooltip = on ? STARTUP_TOOLTIP_ON : STARTUP_TOOLTIP_OFF
  toggle.setAttribute("aria-pressed", on ? "true" : "false")
  toggle.classList.toggle("is-on", on)
  toggle.dataset.tooltip = tooltip
  toggle.setAttribute("aria-label", tooltip)
}

const documentListItem = (doc, { startupVisibility = false, sortable = false } = {}) => {
  const li = document.createElement("li")
  li.dataset.contentDocumentId = String(doc.id)
  if (doc.update_url) li.dataset.updateUrl = doc.update_url

  const main = document.createElement("div")
  main.className = "backoffice-list__main"

  if (sortable) {
    const handle = document.createElement("span")
    handle.className = "backoffice-list__handle"
    handle.dataset.contentDocumentHandle = "true"
    handle.dataset.tooltip = "Déplacez ce doc au sein de la section"
    handle.setAttribute("aria-label", "Déplacez ce doc au sein de la section")
    handle.textContent = "⋮⋮"
    main.appendChild(handle)

    const move = document.createElement("button")
    move.type = "button"
    move.className = "backoffice-list__move"
    move.dataset.contentDocumentMove = "true"
    move.dataset.tooltip = "Déplacez ce doc dans une autre section"
    move.setAttribute("aria-label", "Déplacez ce doc dans une autre section")
    const iconTemplate = document.querySelector("[data-content-docs-move-icon-template]")
    if (iconTemplate?.content) {
      move.appendChild(iconTemplate.content.cloneNode(true))
    } else {
      move.textContent = "⇅"
    }
    main.appendChild(move)
  }

  if (startupVisibility && doc.update_url) {
    const startup = document.createElement("button")
    startup.type = "button"
    startup.className = "backoffice-list__startup"
    startup.dataset.contentDocumentStartup = "true"
    startup.dataset.url = doc.update_url
    const startupTemplate = document.querySelector("[data-content-docs-startup-icon-template]")
    if (startupTemplate?.content) {
      startup.appendChild(startupTemplate.content.cloneNode(true))
    }
    setStartupToggleState(startup, Boolean(doc.visible_to_startups))
    main.appendChild(startup)
  }

  const info = document.createElement("div")
  if (doc.url) {
    const link = document.createElement("a")
    link.href = doc.url
    link.target = "_blank"
    link.rel = "noopener"
    link.className = "backoffice-doc-link"
    link.textContent = doc.title
    info.appendChild(link)
  } else {
    const title = document.createElement("strong")
    title.textContent = doc.title
    info.appendChild(title)
  }
  main.appendChild(info)

  const actions = document.createElement("div")
  actions.className = "backoffice-list__actions"

  const destroy = document.createElement("a")
  destroy.href = doc.destroy_url
  destroy.className = "backoffice-btn backoffice-btn--danger"
  destroy.dataset.contentDocumentDelete = "true"
  destroy.dataset.confirm = "Le document sera définitivement supprimé."
  destroy.textContent = "Supprimer"
  actions.appendChild(destroy)

  li.appendChild(main)
  li.appendChild(actions)
  return li
}

const docsCountLabel = (count) =>
  count > 1 ? `(${count} documents chargés)` : `(${count} document chargé)`

const setCountValue = (el, valueAttr, count, fallbackLabel) => {
  if (!el) return
  const valueEl = el.querySelector(`[${valueAttr}]`)
  if (valueEl) {
    valueEl.textContent = String(count)
    return
  }
  el.textContent = fallbackLabel(count)
}

const syncDocsCount = (root) => {
  if (!root) return
  const group = root.closest(".backoffice-content-group")
  if (!group) return

  const count = root.querySelectorAll("[data-content-document-id]").length
  setCountValue(
    group.querySelector("[data-content-docs-count]"),
    "data-content-docs-count-value",
    count,
    docsCountLabel
  )

  const startupsEl = group.querySelector("[data-content-docs-startups-count]")
  if (startupsEl) {
    const startupsCount = root.querySelectorAll(
      "[data-content-document-startup][aria-pressed='true']"
    ).length
    setCountValue(
      startupsEl,
      "data-content-docs-startups-count-value",
      startupsCount,
      (n) => (n > 1 ? `(${n} visibles startups)` : `(${n} visible startups)`)
    )
  }

  syncEmptyState(root)
}

export const upsertContentDocument = (root, doc, { replace = false } = {}) => {
  if (!root || !doc) return

  const list = ensureList(root)
  if (replace) list.innerHTML = ""

  const existing = list.querySelector(`[data-content-document-id="${doc.id}"]`)
  const item = documentListItem(doc, {
    startupVisibility: supportsStartupVisibility(root),
    sortable: isSortableRoot(root),
  })
  if (existing) existing.replaceWith(item)
  else list.appendChild(item)
  syncDocsCount(root)
}

export const removeContentDocument = (root, documentId) => {
  if (!root) return

  const list = root.querySelector("[data-content-docs-list]")
  list?.querySelector(`[data-content-document-id="${documentId}"]`)?.remove()
  syncDocsCount(root)
}

const resetDocumentForm = (form) => {
  form.reset()
  form.querySelectorAll(".backoffice-file").forEach((wrap) => {
    const name = wrap.querySelector(".backoffice-file__name")
    if (name) {
      name.textContent = "Aucun fichier choisi"
      name.classList.remove("is-filled")
    }
  })
  form.querySelectorAll('input[type="file"]').forEach((input) => {
    input.value = ""
  })
}

const hideDocumentForm = (root) => {
  const wrap = root?.querySelector("[data-content-docs-add-wrap]")
  if (!wrap) return
  const button = wrap.querySelector("[data-content-docs-add]")
  const panel = wrap.querySelector("[data-content-docs-form]")
  if (button) button.hidden = false
  if (panel) panel.hidden = true
}

const showDocumentForm = (root) => {
  const wrap = root?.querySelector("[data-content-docs-add-wrap]")
  if (!wrap) return
  const button = wrap.querySelector("[data-content-docs-add]")
  const panel = wrap.querySelector("[data-content-docs-form]")
  if (button) button.hidden = true
  if (panel) panel.hidden = false
  panel?.querySelector('input[type="text"]')?.focus()
}

const bindDocumentForm = (form) => {
  if (form.dataset.contentDocsBound === "true") return
  form.dataset.contentDocsBound = "true"

  const isSingle = form.dataset.contentDocumentsSingle === "true"

  if (isSingle) {
    form.addEventListener("change", (event) => {
      if (event.target.matches('input[type="file"]') && event.target.files?.length) {
        form.requestSubmit()
      }
    })
  }

  form.addEventListener("submit", async (event) => {
    event.preventDefault()
    const root = form.closest("[data-content-docs]")
    const submit = form.querySelector('input[type="submit"], button[type="submit"]')
    if (submit) submit.disabled = true

    try {
      const response = await fetch(form.action, {
        method: "POST",
        body: new FormData(form),
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-Token": csrfToken() || "",
        },
        credentials: "same-origin",
      })

      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        showBackofficeFlash(data.error || "Erreur d'enregistrement", "alert")
        return
      }

      upsertContentDocument(root, data.document, { replace: isSingle })
      resetDocumentForm(form)
      initBackofficeFileInputs()
      if (isSingle) {
        const label = root?.querySelector("[data-content-single-file-label]")
        if (label) label.textContent = "Remplacer le fichier"
      } else {
        hideDocumentForm(root)
      }
      showBackofficeFlash(data.notice || "Document ajouté.", "notice")
    } catch (_) {
      showBackofficeFlash("Erreur d'enregistrement", "alert")
    } finally {
      if (submit) {
        submit.disabled = false
        submit.removeAttribute("disabled")
      }
    }
  })
}

const bindDocumentAddToggles = () => {
  document.querySelectorAll(".backoffice-body").forEach((body) => {
    if (body.dataset.contentDocsAddBound === "true") return
    body.dataset.contentDocsAddBound = "true"

    body.addEventListener("click", (event) => {
      const addButton = event.target.closest("[data-content-docs-add]")
      if (addButton) {
        event.preventDefault()
        showDocumentForm(addButton.closest("[data-content-docs]"))
        return
      }

      const cancelButton = event.target.closest("[data-content-docs-cancel]")
      if (cancelButton) {
        event.preventDefault()
        const root = cancelButton.closest("[data-content-docs]")
        const form = root?.querySelector("form[data-content-documents]")
        if (form) resetDocumentForm(form)
        hideDocumentForm(root)
      }
    })
  })
}

const bindDocumentStartupVisibility = () => {
  document.querySelectorAll(".backoffice-body").forEach((body) => {
    if (body.dataset.contentDocsStartupBound === "true") return
    body.dataset.contentDocsStartupBound = "true"

    body.addEventListener("click", async (event) => {
      const toggle = event.target.closest("[data-content-document-startup]")
      if (!toggle) return

      event.preventDefault()
      event.stopPropagation()

      const previous = toggle.getAttribute("aria-pressed") === "true"
      const next = !previous
      setStartupToggleState(toggle, next)
      toggle.disabled = true

      try {
        const response = await fetch(toggle.dataset.url, {
          method: "PATCH",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "X-CSRF-Token": csrfToken() || "",
          },
          credentials: "same-origin",
          body: JSON.stringify({ visible_to_startups: next }),
        })

        const data = await response.json().catch(() => ({}))
        if (!response.ok) {
          setStartupToggleState(toggle, previous)
          showBackofficeFlash(data.error || "Erreur d'enregistrement", "alert")
          return
        }

        if (data.document) {
          setStartupToggleState(toggle, Boolean(data.document.visible_to_startups))
        }
        syncDocsCount(toggle.closest("[data-content-docs]"))
        showBackofficeFlash(data.notice || "Visibilité mise à jour.", "notice")
      } catch (_) {
        setStartupToggleState(toggle, previous)
        showBackofficeFlash("Erreur d'enregistrement", "alert")
      } finally {
        toggle.disabled = false
      }
    })
  })
}

const bindDocumentDeletes = () => {
  document.querySelectorAll(".backoffice-body").forEach((body) => {
    if (body.dataset.contentDocsDeleteBound === "true") return
    body.dataset.contentDocsDeleteBound = "true"

    body.addEventListener("click", async (event) => {
      const link = event.target.closest("[data-content-document-delete]")
      if (!link) return

      event.preventDefault()
      event.stopPropagation()

      const confirmed = await confirmBackofficeDelete({
        title: "Supprimer ce document ?",
        text: link.dataset.confirm || "Le document sera définitivement supprimé.",
      })
      if (!confirmed) return

      const root = link.closest("[data-content-docs]")
      const item = link.closest("[data-content-document-id]")
      const documentId = item?.dataset.contentDocumentId

      try {
        const response = await fetch(link.href, {
          method: "DELETE",
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "X-CSRF-Token": csrfToken() || "",
          },
          credentials: "same-origin",
        })

        const data = await response.json().catch(() => ({}))
        if (!response.ok) {
          showBackofficeFlash(data.error || "Erreur de suppression", "alert")
          return
        }

        removeContentDocument(root, documentId)
        showBackofficeFlash(data.notice || "Document supprimé.", "notice")
      } catch (_) {
        showBackofficeFlash("Erreur de suppression", "alert")
      }
    })
  })
}

const listDocumentIds = (list) =>
  Array.from(list.querySelectorAll("[data-content-document-id]"))
    .map((item) => item.dataset.contentDocumentId)
    .filter(Boolean)

const patchReorder = async (root, ids) => {
  const url = root.dataset.reorderUrl
  const section = root.dataset.reorderSection || root.dataset.section
  if (!url || !section) return true

  const body = new FormData()
  ids.forEach((id) => body.append("document_ids[]", id))
  body.append("section", section)
  body.append("_method", "patch")

  const response = await fetch(url, {
    method: "POST",
    body,
    headers: {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-Token": csrfToken() || "",
    },
    credentials: "same-origin",
  })

  return response.ok
}

const patchDocumentSection = async (item, section, position) => {
  const url = item.dataset.updateUrl
  if (!url) return false

  const response = await fetch(url, {
    method: "PATCH",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-Token": csrfToken() || "",
    },
    credentials: "same-origin",
    body: JSON.stringify({ section, position }),
  })

  return response.ok
}

const sortableContentGroups = () =>
  Array.from(document.querySelectorAll("details.backoffice-content-group")).filter((group) =>
    group.querySelector("[data-content-docs-sortable]")
  )

let sectionPickState = null

const clearSectionPickHighlights = () => {
  document
    .querySelectorAll(".backoffice-content-group.is-drop-target")
    .forEach((el) => el.classList.remove("is-drop-target"))
}

const exitSectionPickMode = ({ restoreOpen = true, keepOpenGroup = null } = {}) => {
  if (!sectionPickState) return

  sectionPickState.item?.classList.remove("is-section-pick-source")
  document.body.classList.remove("is-picking-doc-section")
  clearSectionPickHighlights()

  if (restoreOpen && sectionPickState.openState) {
    sectionPickState.openState.forEach(({ group, open }) => {
      group.open = group === keepOpenGroup || open
    })
  }

  sectionPickState = null
}

const startSectionPickMode = (item) => {
  if (!item) return

  if (sectionPickState) exitSectionPickMode({ restoreOpen: true })

  sectionPickState = {
    item,
    fromRoot: item.closest("[data-content-docs]"),
    openState: sortableContentGroups().map((group) => ({
      group,
      open: group.open,
    })),
  }

  item.classList.add("is-section-pick-source")
  sortableContentGroups().forEach((group) => {
    group.open = false
  })
  document.body.classList.add("is-picking-doc-section")
}

const moveDocumentToGroup = async (toGroup) => {
  if (!sectionPickState || !toGroup) return

  const { item, fromRoot } = sectionPickState
  const toRoot = toGroup.querySelector("[data-content-docs-sortable]")
  if (!item || !fromRoot || !toRoot) {
    exitSectionPickMode({ restoreOpen: true })
    return
  }

  if (fromRoot === toRoot) {
    exitSectionPickMode({ restoreOpen: true, keepOpenGroup: toGroup })
    return
  }

  const toList = ensureList(toRoot)
  toList.appendChild(item)
  const newIndex = Math.max(toList.querySelectorAll("[data-content-document-id]").length - 1, 0)
  const targetSection = toRoot.dataset.section

  exitSectionPickMode({ restoreOpen: true, keepOpenGroup: toGroup })

  try {
    const okMove = await patchDocumentSection(item, targetSection, newIndex)
    if (!okMove) {
      ensureList(fromRoot).appendChild(item)
      showBackofficeFlash("Impossible de déplacer le document", "alert")
      syncDocsCount(fromRoot)
      syncDocsCount(toRoot)
      return
    }

    const okTo = await patchReorder(toRoot, listDocumentIds(toList))
    const okFrom = await patchReorder(fromRoot, listDocumentIds(ensureList(fromRoot)))

    syncDocsCount(fromRoot)
    syncDocsCount(toRoot)

    if (!okTo || !okFrom) {
      showBackofficeFlash("Impossible de réordonner les documents", "alert")
      return
    }

    showBackofficeFlash("Document déplacé.", "notice")
  } catch (_) {
    showBackofficeFlash("Erreur lors du déplacement", "alert")
  }
}

const bindDocumentSectionPick = () => {
  document.querySelectorAll(".backoffice-body").forEach((body) => {
    if (body.dataset.contentDocsMoveBound === "true") return
    body.dataset.contentDocsMoveBound = "true"

    body.addEventListener("click", (event) => {
      const moveBtn = event.target.closest("[data-content-document-move]")
      if (moveBtn) {
        event.preventDefault()
        event.stopPropagation()
        const item = moveBtn.closest("[data-content-document-id]")
        startSectionPickMode(item)
        return
      }

      if (!sectionPickState) return

      const group = event.target.closest("details.backoffice-content-group")
      if (group?.querySelector("[data-content-docs-sortable]")) {
        event.preventDefault()
        event.stopPropagation()
        moveDocumentToGroup(group)
        return
      }

      exitSectionPickMode({ restoreOpen: true })
    }, true)

    body.addEventListener("pointermove", (event) => {
      if (!sectionPickState) return
      if (typeof event.clientX !== "number" || typeof event.clientY !== "number") return

      const under = document.elementFromPoint(event.clientX, event.clientY)
      const group = under?.closest?.("details.backoffice-content-group")
      clearSectionPickHighlights()
      if (group?.querySelector("[data-content-docs-sortable]")) {
        group.classList.add("is-drop-target")
      }
    })
  })

  if (document.body.dataset.contentDocsMoveEscBound === "true") return
  document.body.dataset.contentDocsMoveEscBound = "true"
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && sectionPickState) {
      event.preventDefault()
      exitSectionPickMode({ restoreOpen: true })
    }
  })
}

const bindDocumentsSortable = (list) => {
  if (!list || list.dataset.sortableBound === "true") return
  const root = list.closest("[data-content-docs-sortable]")
  if (!root) return

  list.dataset.sortableBound = "true"

  Sortable.create(list, {
    animation: 150,
    handle: "[data-content-document-handle]",
    draggable: "[data-content-document-id]",
    ghostClass: "is-dragging",
    onEnd: async (event) => {
      if (event.from !== event.to) return
      if (event.oldIndex === event.newIndex) return

      const docsRoot = event.from.closest("[data-content-docs]")
      if (!docsRoot) return

      try {
        const ok = await patchReorder(docsRoot, listDocumentIds(event.from))
        if (!ok) {
          showBackofficeFlash("Impossible de réordonner les documents", "alert")
          return
        }
        showBackofficeFlash("Ordre des documents mis à jour.", "notice")
      } catch (_) {
        showBackofficeFlash("Erreur lors du déplacement", "alert")
      }
    },
  })
}

const bindDocumentsSortables = () => {
  document
    .querySelectorAll("[data-content-docs-sortable-list]")
    .forEach((list) => bindDocumentsSortable(list))
}

export const initBackofficeContentDocuments = () => {
  document
    .querySelectorAll("form[data-content-documents]")
    .forEach((form) => bindDocumentForm(form))
  bindDocumentAddToggles()
  bindDocumentStartupVisibility()
  bindDocumentDeletes()
  bindDocumentSectionPick()
  bindDocumentsSortables()
}
